import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, Crown, Target, CalendarRange, LineChart, Lock, History, ChevronDown } from "lucide-react";
import { usePlayerAuth } from "../context/PlayerAuthContext";
import { useProgress } from "../context/ProgressContext";
import { CHAPTERS } from "../data/chapters";
import { ACADEMY_LOGO_H } from "../data/assets";
import PerformanceRing from "../components/elite/PerformanceRing";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOLD = "#D4AF37";

const Block = ({ index, id, icon: Icon, code, title, body, empty, delay }) => (
  <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay }}
    data-testid={`roadmap-block-${id}`}
    className="glass-gold rounded-2xl p-5 sm:p-6">
    <div className="flex items-start justify-between gap-3 mb-3">
      <div className="flex items-center gap-2.5 min-w-0">
        <span className="flex h-9 w-9 items-center justify-center rounded-lg shrink-0" style={{ background: `${GOLD}1a`, border: `1px solid ${GOLD}44` }}>
          <Icon size={17} style={{ color: GOLD }} />
        </span>
        <div className="min-w-0">
          <div className="font-mono text-[10px] tracking-[0.25em]" style={{ color: GOLD }}>{code}</div>
          <h2 className="font-headings text-lg uppercase tracking-wide leading-none mt-0.5">{title}</h2>
        </div>
      </div>
      <span className="font-mono text-[10px] text-white/30 shrink-0 mt-0.5">0{index}/03</span>
    </div>
    <div className="border-t border-dashed pt-3" style={{ borderColor: `${GOLD}2e` }}>
      {body ? (
        <p className="text-sm sm:text-[15px] text-white/80 leading-relaxed whitespace-pre-wrap">{body}</p>
      ) : (
        <p className="font-mono text-xs text-white/35 italic">{empty}</p>
      )}
    </div>
  </motion.div>
);

const DataField = ({ label, value }) => (
  <div className="min-w-0">
    <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#D4AF37]/70">{label}</div>
    <div className="font-mono text-sm text-white break-words">{value}</div>
  </div>
);

const HistoryRow = ({ entry }) => {
  const [open, setOpen] = useState(false);
  const archived = entry.archived_at ? new Date(entry.archived_at).toISOString().slice(0, 10) : "";
  return (
    <div className="glass-card rounded-xl border border-white/10 overflow-hidden" data-testid={`roadmap-history-row`}>
      <button onClick={() => setOpen((o) => !o)} data-testid="roadmap-history-toggle"
        className="w-full flex items-center justify-between gap-3 px-4 py-3.5 text-left hover:bg-white/[0.03] transition-colors">
        <div className="flex items-center gap-2.5 min-w-0">
          <span className="flex h-8 w-8 items-center justify-center rounded-md shrink-0" style={{ background: `${GOLD}12`, border: `1px solid ${GOLD}30` }}>
            <History size={14} style={{ color: GOLD }} />
          </span>
          <div className="min-w-0">
            <div className="font-subheadings font-bold uppercase tracking-wide text-sm truncate">{entry.month || "Previous Month"}</div>
            {archived && <div className="font-mono text-[10px] text-white/35">Archived {archived}</div>}
          </div>
        </div>
        <ChevronDown size={16} className={`text-white/40 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 space-y-3 border-t border-dashed" style={{ borderColor: `${GOLD}22` }} data-testid="roadmap-history-body">
          {[["Focus Goals", entry.focus], ["Drill Schedule", entry.schedule], ["Performance Summary", entry.summary]].map(([label, body]) => (
            <div key={label}>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]/70 mb-1">{label}</div>
              <p className="text-sm text-white/75 leading-relaxed whitespace-pre-wrap">{body || <span className="text-white/30 italic">—</span>}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const RoadmapHistory = ({ history }) => (
  <div className="mt-8" data-testid="roadmap-history">
    <div className="flex items-center gap-2 mb-4">
      <History size={16} style={{ color: GOLD }} />
      <h2 className="font-headings text-xl uppercase tracking-wide">Past Roadmaps</h2>
      <span className="font-mono text-[10px] text-white/30">{history.length} archived</span>
    </div>
    <div className="space-y-2.5">
      {history.map((h, i) => <HistoryRow key={i} entry={h} />)}
    </div>
  </div>
);

export default function Roadmap() {
  const navigate = useNavigate();
  const { player, token } = usePlayerAuth();
  const { completed } = useProgress();
  const [data, setData] = useState(null);
  const [locked, setLocked] = useState(false);
  const pct = Math.round(((completed?.length || 0) / CHAPTERS.length) * 100);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/roadmap`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setData(data))
      .catch((e) => { if (e?.response?.status === 403) setLocked(true); else setData({}); });
  }, [token]);

  if (locked) {
    return (
      <div className="min-h-screen elite-carbon-bg text-white flex items-center justify-center px-6">
        <div className="text-center max-w-sm glass-gold rounded-2xl p-8" data-testid="roadmap-locked">
          <Lock size={30} className="mx-auto mb-4" style={{ color: GOLD }} />
          <h1 className="font-headings text-2xl uppercase tracking-wide mb-2">Elite Members Only</h1>
          <p className="text-white/55 text-sm mb-6">The Monthly Roadmap is part of Elite Coaching. Head back to your Hub to upgrade.</p>
          <button data-testid="roadmap-back-hub" onClick={() => navigate("/hub")} className="bg-[#D4AF37] hover:bg-[#c39f2f] text-black font-bold uppercase tracking-wider text-xs px-6 py-3 rounded-sm">Back to Hub</button>
        </div>
      </div>
    );
  }

  const firstName = (player?.display_name || player?.name || "").split(" ")[0] || "Player";
  const generated = new Date().toISOString().slice(0, 10);

  return (
    <div className="min-h-screen elite-carbon-bg text-white relative with-bottom-nav" data-testid="roadmap-page">
      <div aria-hidden className="pointer-events-none fixed inset-0 z-0 elite-mesh-overlay" />
      <div aria-hidden className="pointer-events-none fixed inset-0 z-0" style={{ background: "radial-gradient(900px 500px at 80% -10%, rgba(212,175,55,0.12), transparent 60%)" }} />
      <div className="relative z-10">
        <header className="sticky top-0 z-30 backdrop-blur-xl border-b px-6 py-4 flex items-center justify-between" style={{ background: "rgba(11,11,11,0.8)", borderColor: `${GOLD}25` }}>
          <button data-testid="roadmap-back" onClick={() => navigate("/hub")} className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-white/60 hover:text-white transition-colors"><ArrowLeft size={15} /> Hub</button>
          <img src={ACADEMY_LOGO_H} alt="The Next O Technical Academy" className="h-9 w-auto object-contain" />
          <span className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider px-2.5 py-2 rounded-sm border" style={{ color: GOLD, borderColor: `${GOLD}55`, background: `${GOLD}12` }}><Crown size={12} /> Elite</span>
        </header>

        <div className="max-w-3xl mx-auto px-6 py-10">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
            <div className="font-mono text-[11px] tracking-[0.3em] mb-2" style={{ color: GOLD }}>// SCOUTING_REPORT</div>
            <h1 className="font-cover text-5xl sm:text-6xl uppercase tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-[#F5E7B8] via-[#D4AF37] to-[#B8912E]">Monthly Roadmap</h1>
          </motion.div>

          {/* Report metadata strip + performance ring */}
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.04 }}
            data-testid="roadmap-report-header" className="glass-card rounded-2xl border border-[#D4AF37]/15 p-5 sm:p-6 mb-6 flex items-center gap-5 sm:gap-8">
            <PerformanceRing percentage={pct} size={104} stroke={10} label="Curriculum" testid="roadmap-progress-ring" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 flex-1 min-w-0">
              <DataField label="Player" value={firstName} />
              <DataField label="Cycle" value={data?.month || "Current"} />
              <DataField label="Chapters" value={`${completed?.length || 0} / ${CHAPTERS.length}`} />
              <DataField label="Generated" value={generated} />
            </div>
          </motion.div>

          <div className="space-y-5">
            <Block index={1} id="focus" code="// FOCUS_GOALS" icon={Target} title="Monthly Focus Goals" body={data?.focus} empty="Coach Barker will set your focus goals here soon." delay={0.08} />
            <Block index={2} id="schedule" code="// DRILL_SCHEDULE" icon={CalendarRange} title="Recommended Drill Schedule" body={data?.schedule} empty="Your recommended drill schedule will appear here." delay={0.14} />
            <Block index={3} id="summary" code="// PERFORMANCE_SUMMARY" icon={LineChart} title="Monthly Performance Summary" body={data?.summary} empty="Your performance summary lands at the end of the month." delay={0.2} />
          </div>

          {Array.isArray(data?.history) && data.history.length > 0 && (
            <RoadmapHistory history={data.history} />
          )}

          <p className="font-mono text-[10px] text-white/25 text-center mt-8 tracking-wider">NEXT O SOCCER · ELITE COACHING · CONFIDENTIAL PLAYER REPORT</p>
        </div>
      </div>
    </div>
  );
}
